import React from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import { Button } from 'rebass';
import classNames from 'classnames';

import { useToastHelper } from 'hooks/useToastHelper';
import services from 'services/services';
import useQuery from 'hooks/useQuery';
import Table from 'components/Table';
import { withBrandingLayout } from 'layouts/BrandingLayout';
import { Skeleton } from 'components/Skeleton';
import Badge from 'components/Badge';
import styles from 'styles/branding-page.module.css';
import useMutation from 'hooks/useMutation';
import { DATE_STYLES, getMomentFormat } from 'utils/dateFormats';

export const BrandingsPage = withBrandingLayout(({ auth }) => {
    const history = useHistory();
    const { addErrorToast, addSuccessToast } = useToastHelper();
    const { data: brandings, isLoading } = useQuery({
        queryFn: () => services.getBrandings(),
        onError: (_, errorMessage) => addErrorToast(errorMessage)
    });
    const { mutate: updateBranding } = useMutation({
        mutationFn: brandingId =>
            services.updateAccount({
                ...auth.account,
                branding_id: brandingId
            }),
        onSuccess: () => {
            addSuccessToast('Default branding updated');
        },
        onError: (_, errorMessage) => addErrorToast(errorMessage)
    });

    if (isLoading) return <BrandingSkeleton />;

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Created</Table.Th>
                        <Table.Th>Default</Table.Th>
                    </Table.Tr>
                </Table.Head>
                <Table.Body>
                    {brandings.map(branding => (
                        <Table.Tr
                            className={classNames('link', styles['branding-row'])}
                            onClick={() => history.push(`/settings/brandings/s/${branding.id}`)}
                            key={branding.id}
                        >
                            <Table.Td className="nowrap text-primary bold">{branding.name}</Table.Td>
                            <Table.Td className="nowrap text-primary">
                                {moment.utc(branding.created_at).format(getMomentFormat(DATE_STYLES.HUMAN_DATE))}
                            </Table.Td>
                            <Table.Td>
                                {auth.account.branding_id === branding.id ? (
                                    <Badge type="main">Default</Badge>
                                ) : (
                                    <Button
                                        variant="secondary-gray"
                                        className={styles['set-default-button']}
                                        onClick={e => {
                                            e.stopPropagation();
                                            updateBranding(branding.id, {
                                                onSuccess: () => {
                                                    auth.account.branding_id = branding.id;
                                                }
                                            });
                                        }}
                                    >
                                        Set as default
                                    </Button>
                                )}
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Body>
            </Table>
            {brandings.length === 0 && <p style={{ textAlign: 'center' }}>No Brandings</p>}
        </>
    );
});

const BrandingSkeleton = () => (
    <Table>
        <Table.Head>
            <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Default</Table.Th>
            </Table.Tr>
        </Table.Head>
        <Table.Body>
            {Array(10)
                .fill(0)
                .map((_, index) => (
                    <Table.Tr className="link" key={index}>
                        <Table.Td className="nowrap text-primary bold">
                            <Skeleton height={22} width={120} />
                        </Table.Td>
                        <Table.Td className="nowrap text-primary">
                            <Skeleton height={22} width={120} />
                        </Table.Td>
                        <Table.Td className="nowrap text-primary">
                            <Skeleton height={22} width={120} />
                        </Table.Td>
                    </Table.Tr>
                ))}
        </Table.Body>
    </Table>
);
